import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, LogOut, Settings as SettingsIcon, User } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { ROLE_META } from "@/lib/team-roles";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export const ProfileMenu = () => {
  const navigate = useNavigate();
  const { workspaces, activeWorkspace, setActiveWorkspaceId } = useWorkspace();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [avatar, setAvatar] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setEmail(user.email ?? "");

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user.id)
        .maybeSingle();

      setFullName(profile?.full_name ?? (user.user_metadata?.full_name as string) ?? "");
      if (profile?.avatar_url) {
        const { data } = await supabase.storage
          .from("avatars")
          .createSignedUrl(profile.avatar_url, 60 * 60);
        setAvatar(data?.signedUrl ?? null);
      }
    };
    load();
  }, []);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Could not sign you out");
      return;
    }
    toast.success("Signed out");
    navigate("/welcome");
  };

  const initials = (fullName || email || "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  const profileTrigger = (
    <Avatar className="h-9 w-9 ring-1 ring-border">
      {avatar && <AvatarImage src={avatar} alt={fullName || email} />}
      <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">{initials}</AvatarFallback>
    </Avatar>
  );

  return (
    <>
    <div className="hidden md:block">
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 px-2">
          {profileTrigger}
          <span className="hidden sm:inline max-w-[140px] truncate text-sm">
            {fullName || email}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 max-h-[min(80vh,600px)] overflow-y-auto bg-popover z-50 shadow-lg">
        <DropdownMenuLabel>
          <p className="font-medium truncate">{fullName || "Your account"}</p>
          <p className="text-xs font-normal text-muted-foreground truncate">{email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/profile")}>
          <User className="mr-2 h-4 w-4" /> Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/settings")}>
          <SettingsIcon className="mr-2 h-4 w-4" /> Settings
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">Workspaces</DropdownMenuLabel>
        {workspaces.length === 0 ? (
          <DropdownMenuItem onClick={() => navigate("/team-management")}>
            Create your first team
          </DropdownMenuItem>
        ) : (
          workspaces.map((w) => (
            <DropdownMenuItem key={w.id} onClick={() => setActiveWorkspaceId(w.id)}>
              <span className="flex-1 truncate">{w.name}</span>
              <Badge variant="outline" className={`ml-2 ${ROLE_META[w.role].badgeClass}`}>
                {ROLE_META[w.role].label}
              </Badge>
              {activeWorkspace?.id === w.id && <Check className="ml-2 h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          ))
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    </div>
    <div className="md:hidden">
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Open account menu">{profileTrigger}</Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto rounded-t-lg border-t bg-card px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5 shadow-2xl">
          <div className="mx-auto mb-5 h-1 w-12 rounded-full bg-border" />
          <SheetHeader className="mb-5 pr-8 text-left">
            <SheetTitle>Your account</SheetTitle>
            <SheetDescription className="break-all">{email}</SheetDescription>
          </SheetHeader>
          <div className="space-y-1 border-b pb-4">
            <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => { setMobileOpen(false); navigate('/profile'); }}><User className="h-5 w-5" />Profile</Button>
            <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => { setMobileOpen(false); navigate('/settings'); }}><SettingsIcon className="h-5 w-5" />Settings</Button>
          </div>
          <div className="py-4">
            <p className="mb-3 text-xs font-semibold uppercase text-muted-foreground">Workspaces</p>
            {workspaces.length === 0 ? (
              <Button variant="outline" className="w-full justify-start" onClick={() => { setMobileOpen(false); navigate('/team-management'); }}>Create your first team</Button>
            ) : workspaces.map((w) => (
              <Button key={w.id} variant="ghost" className={`mb-1 h-auto min-h-12 w-full justify-between gap-2 border px-3 py-3 ${activeWorkspace?.id === w.id ? 'border-primary bg-primary/5' : 'border-transparent'}`} onClick={() => { setActiveWorkspaceId(w.id); setMobileOpen(false); }}>
                <span className="min-w-0 truncate text-left">{w.name}</span>
                <span className="flex shrink-0 items-center gap-2"><Badge variant="outline" className={ROLE_META[w.role].badgeClass}>{ROLE_META[w.role].label}</Badge>{activeWorkspace?.id === w.id && <Check className="h-4 w-4 text-accent" />}</span>
              </Button>
            ))}
          </div>
          <Button variant="outline" className="w-full justify-start gap-3 text-destructive" onClick={() => { setMobileOpen(false); handleLogout(); }}><LogOut className="h-5 w-5" />Sign out</Button>
        </SheetContent>
      </Sheet>
    </div>
    </>
  );
};

export default ProfileMenu;
